import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CarpoolVehicle, DamageReport, Document, TodoItem, HistoryItem } from '../types';
import baserowApi from '../services/baserowApi';
import { 
  ArrowLeft, 
  AlertTriangle, 
  FileText, 
  Users, 
  Image as ImageIcon,
  Edit,
  Trash2,
  Plus,
  CheckCircle,
  AlertCircle,
  Upload,
  Download,
  X,
  CheckSquare,
  Square
} from 'lucide-react';
import { formatDate, isExpiringSoon, isExpired, getDaysUntilExpiry } from '../utils/dateUtils';

// Hilfsfunktionen für Dateiverarbeitung (werden in baserowApi.ts verwendet)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getFileNameWithoutExtension = (filename: string): string => {
  return filename.replace(/\.[^/.]+$/, '');
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getMimeType = (filename: string): string => {
  const extension = filename.toLowerCase().split('.').pop();
  const mimeTypes: { [key: string]: string } = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  };
  return mimeTypes[extension || ''] || 'application/octet-stream';
};

interface VehicleDetailPageProps {
  vehicles: CarpoolVehicle[];
  onVehicleUpdate?: () => Promise<void>;
}

const VehicleDetailPage: React.FC<VehicleDetailPageProps> = ({ vehicles, onVehicleUpdate }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<CarpoolVehicle | null>(null);
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [docSortBy, setDocSortBy] = useState<'date' | 'title'>('date');
  const [docSortOrder, setDocSortOrder] = useState<'desc' | 'asc'>('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [images, setImages] = useState<any[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null);
  const [imageUploadProgress, setImageUploadProgress] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [enhancedUsers, setEnhancedUsers] = useState<any[]>([]);
  
  // Schadensmeldung-State
  const [showDamageForm, setShowDamageForm] = useState(false);
  const [damageForm, setDamageForm] = useState({
    title: '',
    description: '',
    status: 5382, // Gemeldet
    estimated_costs: '',
    real_costs: '',
    notes: '',
    category: 'Schaden'
  });
  const [formTodos, setFormTodos] = useState<TodoItem[]>([]);
  const [formHistory, setFormHistory] = useState<HistoryItem[]>([]);
  const [newFormTodoText, setNewFormTodoText] = useState('');
  const [newFormHistoryText, setNewFormHistoryText] = useState('');
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  
  // Schadensmeldung-Bearbeitung-State
  const [editingDamageId, setEditingDamageId] = useState<number | null>(null);
  const [editDamageForm, setEditDamageForm] = useState<any>(null);
  const [editDamageTodos, setEditDamageTodos] = useState<TodoItem[]>([]);
  const [editDamageHistory, setEditDamageHistory] = useState<HistoryItem[]>([]);
  const [newEditDamageTodoText, setNewEditDamageTodoText] = useState('');
  const [newEditDamageHistoryText, setNewEditDamageHistoryText] = useState('');

  useEffect(() => {
    if (id) {
      loadVehicleData(parseInt(id));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVehicleData = async (vehicleId: number) => {
    try {
      setLoading(true);
      
      // Lade Fahrzeug und Benutzer parallel
      const [freshVehicle, usersResponse] = await Promise.all([
        baserowApi.getVehicle(vehicleId),
        baserowApi.getUsers()
      ]);
      
      if (!freshVehicle) {
        setError('Fahrzeug nicht gefunden');
        return;
      }
      setVehicle(freshVehicle);
      
      // Erstelle eine Map aller Benutzer für schnelle Suche
      const usersById = new Map();
      usersResponse.results.forEach((user: any) => {
        usersById.set(user.id, user);
      });
      
      // Erweitere die Fahrzeug-Benutzer mit vollständigen Namen
      if (freshVehicle.user && Array.isArray(freshVehicle.user)) {
        const enhanced = freshVehicle.user.map((user: any) => {
          const fullUserData = usersById.get(user.id);
          
          let fullName = user.value; // Fallback auf Abkürzung
          let avatarInitials = user.value?.charAt(0) || '?'; // Fallback
          
          if (fullUserData) {
            // Erstelle vollständigen Namen
            if (fullUserData.first_name || fullUserData.last_name) {
              fullName = `${fullUserData.first_name || ''} ${fullUserData.last_name || ''}`.trim();
            } else if (fullUserData.name) {
              fullName = fullUserData.name;
            }
            
            // Erstelle Initialen für Avatar
            if (fullUserData.first_name || fullUserData.last_name) {
              const firstName = fullUserData.first_name || '';
              const lastName = fullUserData.last_name || '';
              avatarInitials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
            } else if (fullUserData.name) {
              const nameParts = fullUserData.name.split(' ');
              if (nameParts.length >= 2) {
                avatarInitials = `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
              } else {
                avatarInitials = fullUserData.name.substring(0, 2).toUpperCase();
              }
            }
            
            // Zeige vollständigen Namen mit Abkürzung in Klammern
            if (fullName && fullName !== user.value) {
              fullName = `${fullName} (${user.value})`;
            }
          }
          
          return {
            ...user,
            fullName: fullName,
            avatarInitials: avatarInitials
          };
        });
        
        setEnhancedUsers(enhanced);
      }

      // Lade Schadensmeldungen für dieses Fahrzeug
      try {
        const damageResponse = await baserowApi.getDamageReports();
        const vehicleDamages = damageResponse.results.filter(
          (damage: DamageReport) => 
            damage.carpool && 
            damage.carpool.some(car => car.id === vehicleId)
        );
        setDamageReports(vehicleDamages);
      } catch (err) {
        console.error('Fehler beim Laden der Schadensmeldungen:', err);
      }

      // Lade Dokumente für dieses Fahrzeug
      try {
        const documentsResponse = await baserowApi.getDocumentsForVehicle(vehicleId);
        // Filtere nur aktive Dokumente
        const activeDocuments = documentsResponse.results.filter((doc: any) => doc.active !== false);
        setDocuments(activeDocuments);
        
        // Extrahiere Bilder aus den Dokumenten (nur aktive)
        const imageDocuments = documentsResponse.results
          .filter((doc: any) => doc.content_type && doc.content_type.startsWith('image/') && doc.active !== false)
          .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        
        // Konvertiere Dokumente zu Bild-Format
        const imageData = imageDocuments.map((doc: any) => ({
          id: doc.id,
          url: doc.file_url,
          name: doc.title,
          size: doc.files?.[0]?.size || 0,
          mime_type: doc.content_type,
          is_image: true,
          uploaded_at: doc.created_at
        }));
        
        console.log('Vehicle images from documents:', imageData);
        setImages(imageData);
      } catch (err) {
        console.error('Fehler beim Laden der Dokumente:', err);
      }

      // Setze Edit-Daten
      setEditData({
        model: freshVehicle.model || '',
        manufacturer: getValue(freshVehicle.manufacturer),
        license_plate: freshVehicle.license_plate || '',
        vin: freshVehicle.vin || '',
        type_of_car: getValue(freshVehicle.type) || '',
        type_of_contract: getValue(freshVehicle.type_of_contract) || '',
        km: freshVehicle.km || 0,
        end_of_contract: freshVehicle.end_of_contract || '',
        contract_end_date: freshVehicle.contract_end_date || '',
        tuev_until: freshVehicle.tuev_until || '',
        monthly_net_sum: freshVehicle.monthly_net_sum || '',
        monthly_gross_sum: freshVehicle.monthly_gross_sum || '',
        poolcar: freshVehicle.poolcar || false,
        taxation: getValue(freshVehicle.taxation)
      });

    } catch (err) {
      console.error('Fehler beim Laden der Fahrzeugdaten:', err);
      setError('Fehler beim Laden der Fahrzeugdaten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: string | undefined): string => {
    if (!value) return '0,00 €';
    const num = parseFloat(value);
    return isNaN(num) ? '0,00 €' : `${num.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
  };

  const getValue = (field: any, fallback = '') => 
    typeof field === 'object' && field !== null ? field.value : field || fallback;

  const getImageUrl = (img: any): string => img?.url || img?.file_url || '';

  const getContractStatus = () => {
    if (!vehicle?.end_of_contract) return null;
    const daysUntilExpiry = getDaysUntilExpiry(vehicle.end_of_contract);
    if (isExpired(vehicle.end_of_contract)) {
      return { text: 'Vertrag abgelaufen', color: 'bg-red-500 text-white' };
    }
    if (isExpiringSoon(vehicle.end_of_contract, 90)) {
      return { text: `${daysUntilExpiry} Tage bis Ablauf`, color: 'bg-yellow-500 text-black' };
    }
    return { text: 'Vertrag aktiv', color: 'bg-green-500 text-white' };
  };

  const getTuvStatus = () => {
    if (!vehicle?.tuev_until) return null;
    const daysUntilExpiry = getDaysUntilExpiry(vehicle.tuev_until);
    if (isExpired(vehicle.tuev_until)) {
      return { text: 'TÜV abgelaufen', color: 'bg-red-500 text-white' };
    }
    if (isExpiringSoon(vehicle.tuev_until, 30)) {
      return { text: `${daysUntilExpiry} Tage bis TÜV`, color: 'bg-yellow-500 text-black' };
    }
    return { text: 'TÜV gültig', color: 'bg-green-500 text-white' };
  };

  const getDamageStatusColor = (status: string) => {
    switch (status) {
      case 'Gemeldet': return 'bg-yellow-500 text-black';
      case 'in Bearbeitung': return 'bg-blue-500 text-white';
      case 'erledigt': return 'bg-green-500 text-white';
      case 'Unbekannt': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Hilfsfunktionen für File Upload...
  const getFileNameWithoutExtension = (fileName: string): string => {
    return fileName.replace(/\.[^/.]+$/, '');
  };

  const getMimeType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'application/pdf';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'gif': return 'image/gif';
      case 'txt': return 'text/plain';
      default: return 'application/octet-stream';
    }
  };

  // Upload-Funktionen...
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !vehicle) return;

    try {
      setUploading(true);
      setUploadProgress('Lade Datei hoch...');

      const uploadResponse = await baserowApi.uploadFile(file);
      setUploadProgress('Datei hochgeladen! Verknüpfe mit Fahrzeug...');

      const documentData = {
        title: getFileNameWithoutExtension(file.name),
        description: `Hochgeladen für ${vehicle.model} (${vehicle.license_plate})`,
        files: [uploadResponse.name],
        active: true,
        content_type: getMimeType(file.name),
        type: 2688,
        carpool: [vehicle.id]
      };

      await baserowApi.createDocument(documentData);
      setUploadProgress('Fertig! Aktualisiere Liste...');

      // Sofortiges Update der Dokumentenliste (schneller als warten)
      const documentsResponse = await baserowApi.getDocumentsForVehicle(vehicle.id);
      setDocuments(documentsResponse.results);

      setUploadProgress('');
      setUploading(false);
      event.target.value = '';

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadProgress('Fehler beim Hochladen');
      setUploading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !vehicle) return;

    try {
      setUploadingImages(true);
      setImageUploadProgress(`Lade ${files.length} Bild(er) hoch...`);

      const imageFiles = Array.from(files);
      
      // Lade jedes Bild einzeln hoch und erstelle Dokument-Einträge
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        setImageUploadProgress(`Lade Bild ${i + 1}/${imageFiles.length} hoch: ${file.name}...`);

        // 1. Datei zu Baserow hochladen
        const uploadResponse = await baserowApi.uploadFile(file);
        
        // 2. Dokument-Eintrag für das Bild erstellen
        const documentData = {
          title: getFileNameWithoutExtension(file.name),
          description: `Fahrzeugbild für ${vehicle.model} (${vehicle.license_plate})`,
          files: [uploadResponse.name],
          active: true,
          content_type: getMimeType(file.name),
          type: 2688, // Gleicher Typ wie andere Dokumente (Fahrzeugbilder werden über content_type unterschieden)
          carpool: [vehicle.id]
        };

        await baserowApi.createDocument(documentData);
      }

      setImageUploadProgress('Bilder hochgeladen! Aktualisiere Anzeige...');
      
      // Lade Dokumente neu, um neue Bilder zu sehen
      const documentsResponse = await baserowApi.getDocumentsForVehicle(vehicle.id);
      // Filtere nur aktive Dokumente
      const activeDocuments = documentsResponse.results.filter((doc: any) => doc.active !== false);
      setDocuments(activeDocuments);
      
      // Filtere Bilder aus den Dokumenten für die Bild-Anzeige (nur aktive)
      const imageDocuments = documentsResponse.results
        .filter((doc: any) => doc.content_type && doc.content_type.startsWith('image/') && doc.active !== false)
        .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
      
      // Konvertiere Dokumente zu Bild-Format für bestehende Anzeige-Logik
      const imageData = imageDocuments.map((doc: any) => ({
        id: doc.id,
        url: doc.file_url,
        name: doc.title,
        size: doc.files?.[0]?.size || 0,
        mime_type: doc.content_type,
        is_image: true,
        uploaded_at: doc.created_at
      }));
      
      setImages(imageData);

      setImageUploadProgress('');
      setUploadingImages(false);
      event.target.value = '';

    } catch (error: any) {
      console.error('Image upload error:', error);
      setImageUploadProgress('Fehler beim Hochladen der Bilder');
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (image: any) => {
    if (!vehicle || !image?.id) return;
    const confirmed = window.confirm('Möchten Sie dieses Bild wirklich löschen?');
    if (!confirmed) return;
    try {
      setDeletingImageId(image.id);
      console.log('Versuche Bild zu löschen:', image.id);
      
      await baserowApi.deleteDocument(image.id);
      console.log('Bild erfolgreich gelöscht');
      
      // Nach dem Löschen neu laden
      const documentsResponse = await baserowApi.getDocumentsForVehicle(vehicle.id);
      // Filtere nur aktive Dokumente
      const activeDocuments = documentsResponse.results.filter((doc: any) => doc.active !== false);
      setDocuments(activeDocuments);
      const imageDocuments = documentsResponse.results
        .filter((doc: any) => doc.content_type && doc.content_type.startsWith('image/') && doc.active !== false)
        .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
      const imageData = imageDocuments.map((doc: any) => ({
        id: doc.id,
        url: doc.file_url,
        name: doc.title,
        size: doc.files?.[0]?.size || 0,
        mime_type: doc.content_type,
        is_image: true,
        uploaded_at: doc.created_at
      }));
      setImages(imageData);
      
      // Falls das aktuell geöffnete Bild gelöscht wurde, schließen
      if (selectedImageIndex !== null && images[selectedImageIndex]?.id === image.id) {
        setSelectedImage(null);
        setSelectedImageIndex(null);
      }
      
      alert('Bild wurde erfolgreich gelöscht!');
    } catch (error: any) {
      console.error('Fehler beim Löschen des Bildes:', error);
      console.error('Error details:', error?.response?.data);
      
      // Detailliertere Fehlermeldung
      let message = 'Das Bild konnte nicht gelöscht werden.';
      if (error?.message?.includes('permissions')) {
        message = 'Keine Berechtigung zum Löschen. Das Bild wurde stattdessen archiviert.';
      } else if (error?.response?.status === 401) {
        message = 'Authentifizierungsfehler. Bitte melden Sie sich erneut an.';
      } else if (error?.response?.status === 403) {
        message = 'Keine Berechtigung zum Löschen. Das Bild wurde möglicherweise archiviert.';
      } else if (error?.message) {
        message = error.message;
      }
      
      alert(message);
      
      // Auch bei Fehlern neu laden, falls Soft-Delete funktioniert hat
      try {
        const documentsResponse = await baserowApi.getDocumentsForVehicle(vehicle.id);
        // Filtere nur aktive Dokumente
        const activeDocuments = documentsResponse.results.filter((doc: any) => doc.active !== false);
        setDocuments(activeDocuments);
        const imageDocuments = documentsResponse.results
          .filter((doc: any) => doc.content_type && doc.content_type.startsWith('image/') && doc.active !== false)
          .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        const imageData = imageDocuments.map((doc: any) => ({
          id: doc.id,
          url: doc.file_url,
          name: doc.title,
          size: doc.files?.[0]?.size || 0,
          mime_type: doc.content_type,
          is_image: true,
          uploaded_at: doc.created_at
        }));
        setImages(imageData);
      } catch (reloadError) {
        console.error('Fehler beim Neuladen nach Löschfehler:', reloadError);
      }
    } finally {
      setDeletingImageId(null);
    }
  };

  const sortedDocuments = useMemo(() => {
    const copy = [...documents];
    copy.sort((a: any, b: any) => {
      if (docSortBy === 'date') {
        const aTime = new Date(a?.created_at || 0).getTime();
        const bTime = new Date(b?.created_at || 0).getTime();
        return docSortOrder === 'desc' ? bTime - aTime : aTime - bTime;
      } else {
        const aTitle = (a?.title || (a as any)?.display || '').toString().toLowerCase();
        const bTitle = (b?.title || (b as any)?.display || '').toString().toLowerCase();
        if (aTitle < bTitle) return docSortOrder === 'asc' ? -1 : 1;
        if (aTitle > bTitle) return docSortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
    return copy;
  }, [documents, docSortBy, docSortOrder]);



  // Fahrzeug speichern
  const handleSaveVehicle = async () => {
    if (!vehicle) return;

    try {
      setSaving(true);
      
      // Prüfe ob sich der Kilometerstand geändert hat und aktualisiere das KM-Check-Datum
      const updateData = { ...editData };
      if (editData.km !== undefined && editData.km !== vehicle.km) {
        updateData.date_of_last_km_check = new Date().toISOString().split('T')[0];
      }
      
      await baserowApi.updateVehicle(vehicle.id, updateData);
      setVehicle({ ...vehicle, ...updateData });
      setIsEditing(false);
      await loadVehicleData(vehicle.id);
      
      // Aktualisiere auch das Dashboard
      if (onVehicleUpdate) {
        await onVehicleUpdate();
      }
    } catch (error: any) {
      console.error('Save vehicle error:', error);
      alert('Fehler beim Speichern des Fahrzeugs: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (!vehicle) return;
    
    setEditData({
      model: vehicle.model || '',
      manufacturer: getValue(vehicle.manufacturer),
      license_plate: vehicle.license_plate || '',
      vin: vehicle.vin || '',
      type_of_car: getValue(vehicle.type) || '',
      type_of_contract: getValue(vehicle.type_of_contract) || '',
      km: vehicle.km || 0,
      end_of_contract: vehicle.end_of_contract || '',
      contract_end_date: vehicle.contract_end_date || '',
      tuev_until: vehicle.tuev_until || '',
      monthly_net_sum: vehicle.monthly_net_sum || '',
      monthly_gross_sum: vehicle.monthly_gross_sum || '',
      poolcar: vehicle.poolcar || false,
      taxation: getValue(vehicle.taxation)
    });
    setIsEditing(false);
  };

  const handleInputChange = (field: string, value: any) => {
    setEditData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Schadensmeldung-Handler
  const handleDamageFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDamageForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Aufgaben-Handler
  const addFormTodo = () => {
    if (newFormTodoText.trim()) {
      setFormTodos(prev => [...prev, { text: newFormTodoText.trim(), done: false }]);
      setNewFormTodoText('');
    }
  };

  const toggleFormTodo = (index: number) => {
    setFormTodos(prev => prev.map((todo, i) => 
      i === index ? { ...todo, done: !todo.done } : todo
    ));
  };

  const removeFormTodo = (index: number) => {
    setFormTodos(prev => prev.filter((_, i) => i !== index));
  };

  // Historie-Handler
  const addFormHistoryEntry = () => {
    if (newFormHistoryText.trim()) {
      const newEntry: HistoryItem = {
        date: new Date().toISOString().split('T')[0],
        text: newFormHistoryText.trim()
      };
      setFormHistory(prev => [...prev, newEntry]);
      setNewFormHistoryText('');
    }
  };

  const removeFormHistoryEntry = (index: number) => {
    setFormHistory(prev => prev.filter((_, i) => i !== index));
  };

  // Bild-Handler für Schadensmeldung
  const handleDamageImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadedImages(prev => [...prev, ...files]);
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Schadensmeldung-Bearbeitung-Handler
  const handleEditDamage = (damage: DamageReport) => {
    setEditingDamageId(damage.id);
    setEditDamageForm({
      title: damage.title || '',
      description: damage.description || '',
      status: damage.status?.id || 5382,
      estimated_costs: damage.estimated_costs || '',
      real_costs: damage.real_costs || '',
      notes: damage.notes || ''
    });
    
    // Parse Todos und Historie (falls vorhanden)
    try {
      const todos = damage.todos ? JSON.parse(damage.todos) : [];
      setEditDamageTodos(todos);
    } catch (e) {
      setEditDamageTodos([]);
    }
    
    try {
      const history = damage.history ? JSON.parse(damage.history) : [];
      setEditDamageHistory(history);
    } catch (e) {
      setEditDamageHistory([]);
    }
  };

  const handleDamageEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditDamageForm((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  const addEditDamageTodo = () => {
    if (newEditDamageTodoText.trim()) {
      setEditDamageTodos(prev => [...prev, { text: newEditDamageTodoText.trim(), done: false }]);
      setNewEditDamageTodoText('');
    }
  };

  const toggleEditDamageTodo = (index: number) => {
    setEditDamageTodos(prev => prev.map((todo, i) => 
      i === index ? { ...todo, done: !todo.done } : todo
    ));
  };

  const removeEditDamageTodo = (index: number) => {
    setEditDamageTodos(prev => prev.filter((_, i) => i !== index));
  };

  const addEditDamageHistoryEntry = () => {
    if (newEditDamageHistoryText.trim()) {
      const newEntry: HistoryItem = {
        date: new Date().toISOString().split('T')[0],
        text: newEditDamageHistoryText.trim()
      };
      setEditDamageHistory(prev => [...prev, newEntry]);
      setNewEditDamageHistoryText('');
    }
  };

  const removeEditDamageHistoryEntry = (index: number) => {
    setEditDamageHistory(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveDamageEdit = async () => {
    if (!editingDamageId || !editDamageForm) return;

    try {
      setSaving(true);
      
      const updateData = {
        title: editDamageForm.title,
        description: `Kategorie: ${editDamageForm.category || 'Schaden'}\n` + editDamageForm.description,
        status: editDamageForm.status,
        estimated_costs: editDamageForm.estimated_costs || "0.00",
        real_costs: editDamageForm.real_costs || "0.00",
        notes: editDamageForm.notes || '',
        todos: JSON.stringify(editDamageTodos),
        history: JSON.stringify(editDamageHistory)
      };

      await baserowApi.updateDamageReport(editingDamageId, updateData);
      
      // Schadensmeldungen neu laden
      const damageResponse = await baserowApi.getDamageReports();
      const vehicleDamages = damageResponse.results.filter(
        (damage: DamageReport) => 
          damage.carpool && 
          damage.carpool.some(car => car.id === vehicle?.id)
      );
      setDamageReports(vehicleDamages);
      
      // Bearbeitung beenden
      setEditingDamageId(null);
      setEditDamageForm(null);
      setEditDamageTodos([]);
      setEditDamageHistory([]);
      setNewEditDamageTodoText('');
      setNewEditDamageHistoryText('');
      
    } catch (error: any) {
      console.error('Fehler beim Aktualisieren der Schadensmeldung:', error);
      alert('Fehler beim Aktualisieren der Schadensmeldung: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDamageEdit = () => {
    setEditingDamageId(null);
    setEditDamageForm(null);
    setEditDamageTodos([]);
    setEditDamageHistory([]);
    setNewEditDamageTodoText('');
    setNewEditDamageHistoryText('');
  };

  const handleDamageFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicle) return;

    try {
      setSaving(true);
      
      // Erweiterte Beschreibung mit Aufgaben und Historie
      let enhancedDescription = damageForm.description;
      
      if (formTodos.length > 0) {
        enhancedDescription += '\n\n**Aufgaben:**\n' + formTodos.map(todo => 
          `- [${todo.done ? 'x' : ' '}] ${todo.text}`
        ).join('\n');
      }
      
      if (formHistory.length > 0) {
        enhancedDescription += '\n\n**Historie:**\n' + formHistory.map(entry => 
          `- ${entry.date}: ${entry.text}`
        ).join('\n');
      }

      const formData = {
        title: damageForm.title,
        description: `Kategorie: ${damageForm.category || 'Schaden'}\n` + enhancedDescription,
        status: damageForm.status,
        estimated_costs: damageForm.estimated_costs || "0.00",
        real_costs: damageForm.real_costs || "0.00",
        carpool: [vehicle.id]
      };

      // Notizen hinzufügen, wenn vorhanden
      if (damageForm.notes) {
        formData.description += '\n\n**Notizen:**\n' + damageForm.notes;
      }

      await baserowApi.createDamageReport(formData);
      
      // Schadensmeldungen neu laden
      const damageResponse = await baserowApi.getDamageReports();
      const vehicleDamages = damageResponse.results.filter(
        (damage: DamageReport) => 
          damage.carpool && 
          damage.carpool.some(car => car.id === vehicle.id)
      );
      setDamageReports(vehicleDamages);
      
      // Form schließen und zurücksetzen
      setShowDamageForm(false);
      setDamageForm({
        title: '',
        description: '',
        status: 5382,
        estimated_costs: '',
        real_costs: '',
        notes: '',
        category: 'Schaden'
      });
      setFormTodos([]);
      setFormHistory([]);
      setNewFormTodoText('');
      setNewFormHistoryText('');
      setUploadedImages([]);
    } catch (error: any) {
      console.error('Fehler beim Erstellen der Schadensmeldung:', error);
      alert('Fehler beim Erstellen der Schadensmeldung: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Lade Fahrzeugdaten...</p>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Fehler</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{error || 'Fahrzeug nicht gefunden'}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Zurück zum Dashboard
          </button>
        </div>
      </div>
    );
  }

  const contractStatus = getContractStatus();
  const tuvStatus = getTuvStatus();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/', { replace: true })}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {vehicle.model}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {getValue(vehicle.manufacturer)} • {vehicle.license_plate}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!isEditing ? (
                <button 
                  onClick={() => setIsEditing(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Edit className="w-4 h-4" />
                  <span>Bearbeiten</span>
                </button>
              ) : (
                <>
                  <button 
                    onClick={handleCancelEdit}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Abbrechen</span>
                  </button>
                  <button 
                    onClick={handleSaveVehicle}
                    disabled={saving}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Speichern...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Speichern</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - All Sections on One Page */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Aktive Schäden</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {damageReports.filter(d => d.status.value !== 'erledigt').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Users className="w-6 h-6 text-blue-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Zugewiesene Fahrer</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {vehicle.user?.length || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <FileText className="w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Dokumente</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {documents.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <ImageIcon className="w-6 h-6 text-purple-500" />
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Bilder</p>
                  <p className="text-xl font-semibold text-gray-900">{images.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Fahrzeugdaten */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-base font-medium text-gray-900 mb-3">Fahrzeugdaten</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Modell</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.model || ''}
                      onChange={(e) => handleInputChange('model', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{vehicle.model}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Hersteller</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.manufacturer || ''}
                      onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{getValue(vehicle.manufacturer)}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kennzeichen</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.license_plate || ''}
                      onChange={(e) => handleInputChange('license_plate', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{vehicle.license_plate}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">FIN</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.vin || ''}
                      onChange={(e) => handleInputChange('vin', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{vehicle.vin || 'Nicht angegeben'}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Fahrzeugtyp</span>
                  {isEditing ? (
                    <select
                      value={editData.type_of_car || ''}
                      onChange={(e) => handleInputChange('type_of_car', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Fahrzeugtyp auswählen</option>
                      <option value="PKW">PKW</option>
                      <option value="LKW">LKW</option>
                      <option value="Transporter">Transporter</option>
                      <option value="Motorrad">Motorrad</option>
                      <option value="Anhänger">Anhänger</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 dark:text-white">{getValue(vehicle.type)}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kilometerstand</span>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editData.km || 0}
                      onChange={(e) => handleInputChange('km', parseInt(e.target.value) || 0)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{vehicle.km?.toLocaleString('de-DE') || 'Nicht angegeben'} km</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contract Info */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-base font-medium text-gray-900 mb-3">Vertragsdaten</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Vertragstyp</span>
                  {isEditing ? (
                    <select
                      value={editData.type_of_contract || ''}
                      onChange={(e) => handleInputChange('type_of_contract', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Vertragstyp auswählen</option>
                      <option value="Leasing">Leasing</option>
                      <option value="Kauf">Kauf</option>
                      <option value="Miete">Miete</option>
                      <option value="Finanzierung">Finanzierung</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 dark:text-white">{getValue(vehicle.type_of_contract)}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Vertragsende</span>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editData.end_of_contract || ''}
                      onChange={(e) => handleInputChange('end_of_contract', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">
                      {vehicle.end_of_contract ? formatDate(vehicle.end_of_contract) : 'Nicht angegeben'}
                    </p>
                  )}
                </div>
                {contractStatus && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</span>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${contractStatus.color}`}>
                      {contractStatus.text}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Monatliche Kosten (Netto)</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.monthly_net_sum || ''}
                      onChange={(e) => handleInputChange('monthly_net_sum', e.target.value)}
                      placeholder="0.00"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{formatCurrency(vehicle.monthly_net_sum)}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Monatliche Kosten (Brutto)</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.monthly_gross_sum || ''}
                      onChange={(e) => handleInputChange('monthly_gross_sum', e.target.value)}
                      placeholder="0.00"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{formatCurrency(vehicle.monthly_gross_sum)}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Poolcar</span>
                  {isEditing ? (
                    <div className="mt-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editData.poolcar || false}
                          onChange={(e) => handleInputChange('poolcar', e.target.checked)}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          Fahrzeug ist ein Poolcar
                        </span>
                      </label>
                    </div>
                  ) : (
                    <p className="text-gray-900 dark:text-white">
                      {vehicle.poolcar ? 'Ja' : 'Nein'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Technical Info */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-base font-medium text-gray-900 mb-3">Technische Daten</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">TÜV bis</span>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editData.tuev_until || ''}
                      onChange={(e) => handleInputChange('tuev_until', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">
                      {vehicle.tuev_until ? formatDate(vehicle.tuev_until) : 'Nicht angegeben'}
                    </p>
                  )}
                </div>
                {tuvStatus && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">TÜV Status</span>
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${tuvStatus.color}`}>
                        {tuvStatus.text}
                      </span>
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Versteuerung</span>
                  {isEditing ? (
                    <select
                      value={editData.taxation || 'Keine Versteuerung'}
                      onChange={(e) => handleInputChange('taxation', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="Keine Versteuerung">Keine Versteuerung</option>
                      <option value="1% Verbrenner">1% Verbrenner</option>
                      <option value="0,5% Hybrid">0,5% Hybrid</option>
                      <option value="0,25% Elektro">0,25% Elektro</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 dark:text-white">{getValue(vehicle.taxation) || 'Keine Versteuerung'}</p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Letzter KM-Check</span>
                  <p className="text-gray-900 dark:text-white">
                    {vehicle.date_of_last_km_check ? formatDate(vehicle.date_of_last_km_check) : 'Nicht angegeben'}
                  </p>
                </div>
              </div>
            </div>
          </div>

      {/* Fahrzeugmanagement&Schäden – Projekte */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
            <span>Fahrzeugmanagement&Schäden</span>
              </h2>
              <button 
                onClick={() => setShowDamageForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
            <span>Neues Projekt</span>
              </button>
            </div>
            
        {damageReports.length === 0 ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Keine Projekte</h3>
            <p className="text-gray-500 dark:text-gray-400">Für dieses Fahrzeug wurden noch keine Projekte erstellt.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {damageReports.map((damage) => (
                  <div key={damage.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{damage.title || 'Unbenannte Schadensmeldung'}</h3>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getDamageStatusColor(damage.status?.value || 'Unbekannt')}`}>
                            {damage.status?.value || 'Unbekannt'}
                          </span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-3">{damage.description}</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Erstellt am:</span>
                            <p className="text-gray-900 dark:text-white">{formatDate(damage["Erstellt am"])}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Geschätzte Kosten:</span>
                            <p className="text-gray-900 dark:text-white">{formatCurrency(damage.estimated_costs)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Tatsächliche Kosten:</span>
                            <p className="text-gray-900 dark:text-white">{formatCurrency(damage.real_costs)}</p>
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleEditDamage(damage)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Edit className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Zugewiesene Fahrer */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Users className="w-6 h-6 text-blue-500" />
                <span>Zugewiesene Fahrer</span>
              </h2>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Fahrer zuweisen</span>
              </button>
            </div>
            
            {!(Array.isArray(vehicle.user) && vehicle.user.length > 0) ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Keine Fahrer zugewiesen</h3>
                <p className="text-gray-500 dark:text-gray-400">Diesem Fahrzeug sind noch keine Fahrer zugewiesen.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicle.user.map((user: any) => (
                  <div key={user.id || user.value} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium">
                          {(() => {
                            const enhancedUser = enhancedUsers.find(u => u.id === user.id);
                            return enhancedUser?.avatarInitials || user.value?.charAt(0) || '?';
                          })()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {(() => {
                            const enhancedUser = enhancedUsers.find(u => u.id === user.id);
                            return enhancedUser?.fullName || user.value || 'Unbekannter Fahrer';
                          })()}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Fahrer</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">ID:</span>
                        <p className="text-gray-900 dark:text-white">{user.id}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dokumente */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <FileText className="w-6 h-6 text-green-500" />
                <span>Dokumente</span>
              </h2>
              <div className="flex items-center space-x-4">
                {/* Sort Controls */}
                <div className="hidden md:flex items-center space-x-2">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Sortierung</label>
                  <select
                    value={docSortBy}
                    onChange={(e) => setDocSortBy(e.target.value as 'date' | 'title')}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="date">Datum</option>
                    <option value="title">Titel</option>
                  </select>
                  <select
                    value={docSortOrder}
                    onChange={(e) => setDocSortOrder(e.target.value as 'asc' | 'desc')}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="desc">Absteigend</option>
                    <option value="asc">Aufsteigend</option>
                  </select>
                </div>
                {uploading && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>{uploadProgress}</span>
                  </div>
                )}
                <label className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>Dokument hochladen</span>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>
            
            {!documents || documents.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Keine Dokumente</h3>
                <p className="text-gray-500 dark:text-gray-400">Für dieses Fahrzeug wurden noch keine Dokumente hochgeladen.</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-hidden">
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/3">Titel</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/3">Beschreibung</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/6">Hochgeladen am</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 w-1/6">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedDocuments.map((doc) => {
                        const anyDoc: any = doc as any;
                        const url = anyDoc.file_url || (anyDoc.files && anyDoc.files[0]?.url) || null;
                        return (
                          <tr key={doc.id} className="hover:bg-gray-50">
                            <td className="py-2 px-3 text-gray-900 w-1/3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                <span className="truncate">{anyDoc.title || anyDoc.display || 'Unbenanntes Dokument'}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-gray-600 w-1/3">
                              <span className="truncate block">{anyDoc.description && anyDoc.description !== 'Keine Beschreibung' ? anyDoc.description : ''}</span>
                            </td>
                            <td className="py-2 px-3 text-gray-600 w-1/6">
                              {anyDoc.created_at ? new Date(anyDoc.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                            </td>
                            <td className="py-2 px-3 text-right w-1/6">
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                >
                                  <Download className="w-4 h-4" />
                                  Öffnen
                                </a>
                              ) : (
                                <span className="text-gray-400">Keine Datei</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Fahrzeugbilder */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <ImageIcon className="w-6 h-6 text-purple-500" />
                <span>Fahrzeugbilder</span>
              </h2>
              <div className="flex items-center space-x-4">
                {uploadingImages && (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">{imageUploadProgress}</span>
                  </div>
                )}
                <label className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 cursor-pointer">
                  <Plus className="w-4 h-4" />
                  <span>Bilder hinzufügen</span>
                  <input
                    type="file"
                    onChange={handleImageUpload}
                    className="hidden"
                    accept="image/*"
                    multiple
                    disabled={uploadingImages}
                  />
                </label>
              </div>
            </div>
            
            {!images || images.length === 0 ? (
              <div className="text-center py-8">
                <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Keine Bilder</h3>
                <p className="text-gray-500 dark:text-gray-400">Für dieses Fahrzeug wurden noch keine Bilder hochgeladen.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {images.map((image, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-200">
                    <div className="relative">
                      <img
                        src={getImageUrl(image)}
                        alt={image.name || `Fahrzeugbild ${index + 1}`}
                        className="w-full h-48 object-cover transition-transform duration-200 group-hover:scale-105"
                        onClick={() => { setSelectedImage(getImageUrl(image)); setSelectedImageIndex(index); }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNHB4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+QmlsZCBuaWNodCB2ZXJmw7xnYmFyPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center pointer-events-none">
                        <div className="opacity-0 group-hover:opacity-100 bg-white bg-opacity-90 text-gray-800 px-3 py-1 rounded-full text-sm font-medium transition-all duration-200">
                          Vergrößern
                        </div>
                      </div>
                      {/* Delete button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteImage(image); }}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60"
                        title="Bild löschen"
                        disabled={deletingImageId === image.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {image.name || `Bild ${index + 1}`}
                      </p>
                      {image.uploaded_at && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(image.uploaded_at).toLocaleDateString('de-DE')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Schadensmeldung Modal */}
      {showDamageForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-xl shadow-xl bg-white dark:bg-gray-800">
            <form onSubmit={handleDamageFormSubmit} className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Neues Projekt</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Kategorie</label>
                  <select
                    value={damageForm.category || 'Schaden'}
                    onChange={(e) => setDamageForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option>Schaden</option>
                    <option>Termin</option>
                    <option>Service & Inspektion</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Titel</label>
                  <input
                    name="title"
                    value={damageForm.title}
                    onChange={handleDamageFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                    placeholder="z.B. Steinschlag Windschutzscheibe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Fahrzeug</label>
                  <input
                    value={vehicle ? `${vehicle.license_plate} - ${vehicle.model}` : ''}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white"
                    disabled
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Beschreibung</label>
                  <textarea
                    name="description"
                    value={damageForm.description}
                    onChange={handleDamageFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={3}
                    required
                    placeholder="Detaillierte Beschreibung des Schadens..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Geschätzte Kosten (€)</label>
                  <input
                    name="estimated_costs"
                    type="number"
                    step="0.01"
                    value={damageForm.estimated_costs}
                    onChange={handleDamageFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Tatsächliche Kosten (€)</label>
                  <input
                    name="real_costs"
                    type="number"
                    step="0.01"
                    value={damageForm.real_costs}
                    onChange={handleDamageFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Notizen</label>
                  <textarea
                    name="notes"
                    value={damageForm.notes}
                    onChange={handleDamageFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={2}
                    placeholder="Zusätzliche Notizen..."
                  />
                </div>
                
                {/* Aufgaben-Sektion */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Aufgaben</label>
                  <div className="space-y-2">
                    {formTodos.map((todo, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleFormTodo(index)}
                          className="flex items-center"
                        >
                          {todo.done ? <CheckSquare className="w-4 h-4 text-green-500" /> : <Square className="w-4 h-4" />}
                        </button>
                        <span className={todo.done ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}>{todo.text}</span>
                        <button
                          type="button"
                          onClick={() => removeFormTodo(index)}
                          className="ml-auto text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFormTodoText}
                        onChange={(e) => setNewFormTodoText(e.target.value)}
                        placeholder="Neue Aufgabe..."
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFormTodo())}
                      />
                      <button
                        type="button"
                        onClick={addFormTodo}
                        className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                {/* Historie-Sektion */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Historie</label>
                  <div className="space-y-2">
                    {formHistory.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{entry.date}:</span>
                        <span className="flex-1 text-gray-900 dark:text-white">{entry.text}</span>
                        <button
                          type="button"
                          onClick={() => removeFormHistoryEntry(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFormHistoryText}
                        onChange={(e) => setNewFormHistoryText(e.target.value)}
                        placeholder="Neuer Historie-Eintrag..."
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFormHistoryEntry())}
                      />
                      <button
                        type="button"
                        onClick={addFormHistoryEntry}
                        className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Bilder hochladen</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleDamageImageUpload}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {uploadedImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {uploadedImages.map((file, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Upload ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6 flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Erstellen...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Erstellen</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDamageForm(false);
                    // Form komplett zurücksetzen
                    setDamageForm({
                      title: '',
                      description: '',
                      status: 5382,
                      estimated_costs: '',
                      real_costs: '',
                      notes: '',
                      category: 'Schaden'
                    });
                    setFormTodos([]);
                    setFormHistory([]);
                    setNewFormTodoText('');
                    setNewFormHistoryText('');
                    setUploadedImages([]);
                  }}
                  className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schadensmeldung-Bearbeitung Modal */}
      {editingDamageId && editDamageForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-xl shadow-xl bg-white dark:bg-gray-800">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Projekt bearbeiten</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Kategorie</label>
                  <select
                    value={editDamageForm.category || 'Schaden'}
                    onChange={(e) => setEditDamageForm((prev: any) => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option>Schaden</option>
                    <option>Termin</option>
                    <option>Service & Inspektion</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Titel</label>
                  <input
                    name="title"
                    value={editDamageForm.title}
                    onChange={handleDamageEditFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Status</label>
                  <select
                    name="status"
                    value={editDamageForm.status}
                    onChange={handleDamageEditFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value={5382}>Gemeldet</option>
                    <option value={5383}>In Bearbeitung</option>
                    <option value={5384}>Erledigt</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Beschreibung</label>
                  <textarea
                    name="description"
                    value={editDamageForm.description}
                    onChange={handleDamageEditFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={3}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Geschätzte Kosten (€)</label>
                  <input
                    name="estimated_costs"
                    type="number"
                    step="0.01"
                    value={editDamageForm.estimated_costs}
                    onChange={handleDamageEditFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Tatsächliche Kosten (€)</label>
                  <input
                    name="real_costs"
                    type="number"
                    step="0.01"
                    value={editDamageForm.real_costs}
                    onChange={handleDamageEditFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Notizen</label>
                  <textarea
                    name="notes"
                    value={editDamageForm.notes}
                    onChange={handleDamageEditFormChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={2}
                  />
                </div>
                
                {/* Aufgaben-Sektion */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Aufgaben</label>
                  <div className="space-y-2">
                    {editDamageTodos.map((todo, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleEditDamageTodo(index)}
                          className="flex items-center"
                        >
                          {todo.done ? <CheckSquare className="w-4 h-4 text-green-500" /> : <Square className="w-4 h-4" />}
                        </button>
                        <span className={todo.done ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}>{todo.text}</span>
                        <button
                          type="button"
                          onClick={() => removeEditDamageTodo(index)}
                          className="ml-auto text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newEditDamageTodoText}
                        onChange={(e) => setNewEditDamageTodoText(e.target.value)}
                        placeholder="Neue Aufgabe..."
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEditDamageTodo())}
                      />
                      <button
                        type="button"
                        onClick={addEditDamageTodo}
                        className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                {/* Historie-Sektion */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Historie</label>
                  <div className="space-y-2">
                    {editDamageHistory.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{entry.date}:</span>
                        <span className="flex-1 text-gray-900 dark:text-white">{entry.text}</span>
                        <button
                          type="button"
                          onClick={() => removeEditDamageHistoryEntry(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newEditDamageHistoryText}
                        onChange={(e) => setNewEditDamageHistoryText(e.target.value)}
                        placeholder="Neuer Historie-Eintrag..."
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEditDamageHistoryEntry())}
                      />
                      <button
                        type="button"
                        onClick={addEditDamageHistoryEntry}
                        className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex gap-2">
                <button
                  onClick={handleSaveDamageEdit}
                  disabled={saving}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Speichern...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Speichern</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelDamageEdit}
                  className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bildvergrößerung Modal mit Navigation */}
      {selectedImage && selectedImageIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
            {/* Prev Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!images || images.length === 0 || selectedImageIndex === null) return;
                const prevIndex = (selectedImageIndex - 1 + images.length) % images.length;
                setSelectedImage(getImageUrl(images[prevIndex]));
                setSelectedImageIndex(prevIndex);
              }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full"
              aria-label="Vorheriges Bild"
            >
              ‹
            </button>
            <img
              src={selectedImage}
              alt="Vergrößertes Bild"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Next Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!images || images.length === 0 || selectedImageIndex === null) return;
                const nextIndex = (selectedImageIndex + 1) % images.length;
                setSelectedImage(getImageUrl(images[nextIndex]));
                setSelectedImageIndex(nextIndex);
              }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full"
              aria-label="Nächstes Bild"
            >
              ›
            </button>
            <button
              onClick={() => { setSelectedImage(null); setSelectedImageIndex(null); }}
              className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all duration-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetailPage; 